/**
 * `/scan` — der Beleg am Tresen.
 *
 * Ablauf 2: Tanken, Baumarkt, Parkticket. Der Kurzbefehl auf dem iPhone kann
 * das schon; was er nicht kann, ist zeigen, **dass es angekommen ist**. Genau
 * dafuer gibt es diese Seite — und dafuer liegt ein Manifest im `public/`,
 * damit sie als Knopf auf dem Startbildschirm liegt.
 *
 * Das Entscheidende steht in `lib/scan.ts`: **mehrere Bilder werden EIN
 * Beleg.** Drei Fotos einer dreiseitigen Rechnung waeren sonst drei Belege mit
 * dreimal „Betrag fehlt", von denen man zwei von Hand ablehnen muesste
 * (§15.2). Der Server fuegt sie zu einem PDF zusammen; diese Seite schickt sie
 * in **einem** Aufruf und in der Reihenfolge, in der sie hier stehen.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, ChevronDown, ChevronUp, FileText, ImagePlus, Trash2 } from 'lucide-react';

import { bh, fehlerText } from '@/lib/api';
import { dateiGroesse } from '@/lib/format';
import {
  ABWEIS_TEXT,
  MAX_SEITEN,
  seiteEntfernen,
  seiteVerschieben,
  seitenHinzufuegen,
  stapelText,
  type Seite,
} from '@/lib/scan';
import { Button } from '@/components/ui/Button';
import { Seitenkopf } from '@/components/ui/Feld';
import { useToast } from '@/components/ui/Toast';
import { Band } from '@/components/ui/Zustaende';
import type { UploadErgebnis } from '@/types';

export function ScanPage() {
  const cache = useQueryClient();
  const toast = useToast();

  const [seiten, setSeiten] = useState<Seite[]>([]);
  const [fortschritt, setFortschritt] = useState<number | null>(null);
  const [angekommen, setAngekommen] = useState<UploadErgebnis | null>(null);

  const kamera = useRef<HTMLInputElement>(null);
  const datei = useRef<HTMLInputElement>(null);

  // Die `blob:`-Adressen der Miniaturen wieder freigeben, sonst behaelt der
  // Browser jedes Foto der Sitzung im Speicher.
  useEffect(() => {
    return () => {
      seiten.forEach((seite) => {
        if (seite.vorschauUrl) URL.revokeObjectURL(seite.vorschauUrl);
      });
    };
    // Absichtlich nur beim Verlassen der Seite: waeren `seiten` in den
    // Abhaengigkeiten, gaebe der Effekt bei jedem Hinzufuegen die Adressen
    // frei, die er gerade erst gebaut hat.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aufnehmen = useCallback(
    (dateien: FileList | null) => {
      if (!dateien || dateien.length === 0) return;

      const neue: Seite[] = Array.from(dateien).map((d) => ({
        id: `${d.name}-${d.size}-${Math.random().toString(36).slice(2, 8)}`,
        name: d.name,
        groesse: d.size,
        mimeType: d.type,
        datei: d,
        vorschauUrl: d.type.startsWith('image/') ? URL.createObjectURL(d) : null,
      }));

      const ergebnis = seitenHinzufuegen(seiten, neue);
      setSeiten(ergebnis.seiten);
      setAngekommen(null);

      // Abgewiesenes nicht still verschlucken — wer drei Fotos schickt und
      // zwei davon nie wiedersieht, glaubt, sie seien hochgeladen.
      ergebnis.abgewiesen.forEach((ab) =>
        toast.fehler(`${ab.name}: ${ABWEIS_TEXT[ab.grund]}`),
      );

      // Die freigegebenen Adressen der abgewiesenen Seiten aufraeumen.
      neue
        .filter((s) => !ergebnis.seiten.includes(s) && s.vorschauUrl)
        .forEach((s) => URL.revokeObjectURL(s.vorschauUrl!));
    },
    [seiten, toast],
  );

  const hochladen = useMutation({
    mutationFn: () => {
      setFortschritt(0);
      return bh.hochladen(
        seiten.map((s) => s.datei),
        {},
        setFortschritt,
      );
    },
    onSuccess: (ergebnis) => {
      setFortschritt(null);
      setSeiten([]);
      setAngekommen(ergebnis);
      void cache.invalidateQueries({ queryKey: ['belege'] });
      toast.erfolg(
        ergebnis.duplikat_von
          ? 'Angekommen — dieser Beleg lag schon da.'
          : 'Angekommen.',
      );
    },
    onError: (fehler) => {
      setFortschritt(null);
      toast.fehler(fehlerText(fehler));
    },
  });

  return (
    <div className="mx-auto max-w-2xl">
      <Seitenkopf
        titel="Scan"
        beschreibung="Beleg fotografieren und einschicken. Mehrere Bilder werden ein Beleg."
      />

      <input
        ref={kamera}
        type="file"
        accept="image/*,application/pdf"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => {
          aufnehmen(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={datei}
        type="file"
        accept="image/*,application/pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          aufnehmen(e.target.files);
          e.target.value = '';
        }}
      />

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => kamera.current?.click()} disabled={hochladen.isPending}>
          <Camera size={16} className="mr-2" />
          Foto aufnehmen
        </Button>
        <Button
          variant="outline"
          onClick={() => datei.current?.click()}
          disabled={hochladen.isPending}
        >
          <ImagePlus size={16} className="mr-2" />
          Datei wählen
        </Button>
      </div>

      {angekommen && (
        <div className="mt-4 rounded-xl border border-success-200 bg-success-50 px-5 py-4">
          <p className="text-sm font-medium text-success-700">
            Angekommen{angekommen.seiten > 1 ? ` — ${angekommen.seiten} Seiten, ein Beleg` : ''}.
          </p>
          <p className="mt-0.5 text-sm text-success-700/80">
            {angekommen.duplikat_von
              ? 'Denselben Inhalt gab es schon — es wurde nichts doppelt angelegt.'
              : 'Der Beleg wird jetzt ausgelesen und abgelegt.'}
          </p>
          <Link
            to={`/eingang/${angekommen.document.id}`}
            className="mt-2 inline-block text-sm font-medium text-success-700 underline"
          >
            Zum Beleg
          </Link>
        </div>
      )}

      {seiten.length > 0 && (
        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-medium text-slate-800">{stapelText(seiten.length)}</p>
            <p className="text-xs text-slate-400">höchstens {MAX_SEITEN}</p>
          </div>

          <ol className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {seiten.map((seite, i) => (
              <li key={seite.id} className="rounded-lg border border-slate-200 p-2">
                <div className="flex h-32 items-center justify-center overflow-hidden rounded bg-slate-50">
                  {seite.vorschauUrl ? (
                    <img
                      src={seite.vorschauUrl}
                      alt={`Seite ${i + 1}`}
                      className="max-h-32 object-contain"
                    />
                  ) : (
                    <FileText size={22} className="text-slate-300" />
                  )}
                </div>
                <p className="mt-2 truncate text-xs text-slate-600" title={seite.name}>
                  {i + 1}. {seite.name}
                </p>
                <p className="text-xs text-slate-400">{dateiGroesse(seite.groesse)}</p>
                <div className="mt-1.5 flex items-center gap-1">
                  <button
                    type="button"
                    title="nach vorn"
                    disabled={i === 0 || hochladen.isPending}
                    onClick={() => setSeiten(seiteVerschieben(seiten, seite.id, -1))}
                    className="rounded p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-30"
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    type="button"
                    title="nach hinten"
                    disabled={i === seiten.length - 1 || hochladen.isPending}
                    onClick={() => setSeiten(seiteVerschieben(seiten, seite.id, 1))}
                    className="rounded p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-30"
                  >
                    <ChevronDown size={14} />
                  </button>
                  <button
                    type="button"
                    title="entfernen"
                    disabled={hochladen.isPending}
                    onClick={() => {
                      if (seite.vorschauUrl) URL.revokeObjectURL(seite.vorschauUrl);
                      setSeiten(seiteEntfernen(seiten, seite.id));
                    }}
                    className="ml-auto rounded p-1 text-slate-400 hover:bg-danger-50 hover:text-danger-600 disabled:opacity-30"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ol>

          {fortschritt !== null && (
            <div className="mt-4">
              <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-primary-600 transition-[width] duration-200"
                  style={{ width: `${fortschritt}%` }}
                />
              </div>
              <p className="mt-1.5 text-xs text-slate-500">wird hochgeladen … {fortschritt} %</p>
            </div>
          )}

          <div className="mt-4 flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                seiten.forEach((s) => s.vorschauUrl && URL.revokeObjectURL(s.vorschauUrl));
                setSeiten([]);
              }}
              disabled={hochladen.isPending}
            >
              Verwerfen
            </Button>
            <Button onClick={() => hochladen.mutate()} loading={hochladen.isPending}>
              Einschicken
            </Button>
          </div>
        </div>
      )}

      <div className="mt-5">
        <Band>
          Mehrere Fotos einer mehrseitigen Rechnung ergeben <strong>einen</strong> Beleg — die
          Reihenfolge oben ist die Seitenreihenfolge. Ein PDF ist schon ein fertiger Beleg und
          geht einzeln.
        </Band>
      </div>
    </div>
  );
}
